import React, { useState } from 'react';
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import { LayoutDashboard, BookOpen, Layers, Ticket, MessageSquareQuote, Users, GraduationCap, Mail, ShieldCheck, Settings, LogOut, ExternalLink, Menu, X, FileText, Send, Bot, ClipboardList } from 'lucide-react';
import { useAuth } from '../../lib/auth';
import BrandLogo from '../../components/BrandLogo';

const NAV = [
  { to: '/admin', end: true, label: 'Dashboard', icon: LayoutDashboard, key: null },
  { to: '/admin/courses', label: 'Courses', icon: BookOpen, key: 'courses' },
  { to: '/admin/categories', label: 'Categories', icon: Layers, key: 'categories' },
  { to: '/admin/vouchers', label: 'Vouchers', icon: Ticket, key: 'vouchers' },
  { to: '/admin/testimonials', label: 'Testimonials', icon: MessageSquareQuote, key: 'testimonials' },
  { to: '/admin/users', label: 'Users', icon: Users, key: 'users' },
  { to: '/admin/enrollments', label: 'Enrollments', icon: GraduationCap, key: 'enrollments' },
  { to: '/admin/contact', label: 'Contact', icon: Mail, key: 'contact' },
  { to: '/admin/admins', label: 'Admins', icon: ShieldCheck, key: 'admins', superOnly: true },
  { to: '/admin/content', label: 'Site Content', icon: FileText, key: 'settings' },
  { to: '/admin/smtp', label: 'SMTP / Email', icon: Send, key: 'settings' },
  { to: '/admin/chatbot', label: 'AI Chatbot', icon: Bot, key: 'settings' },
  { to: '/admin/dod-8140', label: 'DoD 8140', icon: ClipboardList, key: 'settings' },
  { to: '/admin/settings', label: 'Site Settings', icon: Settings, key: 'settings' },
];

export default function AdminLayout() {
  const { user, logout, hasPerm, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const items = NAV.filter(n => {
    if (n.superOnly) return isSuperAdmin;
    if (!n.key) return true;
    return hasPerm(n.key);
  });

  const handleLogout = () => { logout(); navigate('/'); };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <aside className={`fixed lg:sticky top-0 left-0 h-screen w-64 bg-gradient-to-b from-[#1a0e3d] via-[#241245] to-[#0f0d1a] text-white flex flex-col z-40 transition-transform ${open ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
        <div className="px-6 py-5 border-b border-white/10 flex items-center justify-between">
          <Link to="/admin" className="flex items-center gap-2">
            <BrandLogo size="md" showWordmark={false} />
            <div>
              <div className="font-extrabold text-white">Skillbridge.guru<span className="text-[#ff5722]"> admin</span></div>
              <div className="text-[10px] uppercase tracking-widest text-purple-300">Control panel</div>
            </div>
          </Link>
          <button onClick={() => setOpen(false)} className="lg:hidden"><X size={20} /></button>
        </div>

        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
          {items.map(item => (
            <NavLink key={item.to} to={item.to} end={item.end} onClick={() => setOpen(false)}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold transition-all ${isActive ? 'bg-gradient-to-r from-[#6b47dc] to-[#ff5722] text-white shadow-lg' : 'text-purple-200 hover:bg-white/5 hover:text-white'}`}>
              <item.icon size={18} /> {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="p-3 border-t border-white/10">
          <Link to="/" className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-purple-200 hover:bg-white/5">
            <ExternalLink size={16} /> View site
          </Link>
          <button onClick={handleLogout} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-red-300 hover:bg-red-500/10">
            <LogOut size={16} /> Sign out
          </button>
          <div className="px-3 py-2 mt-1 text-[11px] text-purple-300">
            <div className="font-bold truncate">{user?.name}</div>
            <div className="truncate opacity-70">{user?.email}</div>
            <div className="mt-1 inline-block px-1.5 py-0.5 rounded bg-white/10 uppercase font-bold text-[9px] tracking-wider">{user?.role?.replace('_', ' ')}</div>
          </div>
        </div>
      </aside>

      <div className="flex-1 min-w-0">
        <header className="lg:hidden sticky top-0 z-30 bg-white border-b border-gray-200 px-4 h-14 flex items-center justify-between">
          <button onClick={() => setOpen(true)}><Menu size={22} /></button>
          <div className="font-bold">Admin Panel</div>
          <div className="w-6" />
        </header>
        <div className="p-4 md:p-8">
          <Outlet />
        </div>
      </div>

      {open && <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setOpen(false)} />}
    </div>
  );
}
